import { useEffect, useRef, useState } from "react";
import { Box, Button, FormHelperText, IconButton, TextField, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Trans, useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import styles from "./AddMediaModal.styles";
import SecureSyncFileUpload from "src/components/common/SecureSyncFileUpload/SecureSyncFileUpload";
import SecureSyncTable from "src/components/common/SecureSyncTable/SecureSyncTable";
import SecureSyncIconTooltip from "src/components/common/SecureSyncIconTooltip/SecureSyncIconTooltip";
import SecureSyncSelect from "src/components/common/SecureSyncSelect/SecureSyncSelect";
import SecureSyncModal from "src/components/common/SecureSyncModal/SecureSyncModal";
import UploadIcon from "src/components/Icons/UploadIcon/UploadIcon";
import SecureSyncAlert from "src/components/common/SecureSyncAlert/SecureSyncAlert";
import { downloadInstructionGuide, handleFileRead } from "src/utils/processMediaHelper";
import SecureSyncReadOnlyTextField from "src/components/common/SecureSyncReadOnlyTextField/SecureSyncReadOnlyTextField";
import DeleteAllConfirmation from "src/components/Operations/DisasterRecovery/ConfirmationModals/DeleteConfirmation/DeleteConfirmation";
import CloseModalConfirmation from "src/components/ProcessMedia/ConfirmationModals/CloseModalConfirmation/CloseModalConfirmation";
import { enterKeyCode, sourceType, getSourceTypeOptions } from "src/constants/processMediaConstants";
import SecureSyncTableCard from "src/components/common/SecureSyncTableCard/SecureSyncTableCard";
import CircularLoaderFullPage from "src/components/common/CircularLoaderFullPage/CircularLoaderFullPage";
import { importMediaFileFileName, staticFile } from "src/constants/fileConstants";
import { useSecureSyncHotKeys } from "src/customHooks";
import { getKeyBoardLabel } from "src/utils/commonHelper";
import { getShortCutKeys } from "src/constants/shortCutKeys";

const AddMediaModal = ({ onClose, onSubmit, volserMaxLength = "20", addedMediaNos }) => {
    const scannerRef = useRef(null);
    const shortCutKeys = getShortCutKeys();
    useSecureSyncHotKeys({
        hotkeys: shortCutKeys.ok,
        callback: () => {
            handleOkClick();
        },
    });
    const { t } = useTranslation();

    const [mediaType, setMediaType] = useState(sourceType.keyboard);
    const [fileFormatError, setFileFormatError] = useState({
        fileName: "",
        hasError: false,
        lastRecordCount: "",
    });
    const [mediaTypeList, setMediaTypeList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showCloseModalConfirmation, setShowCloseModalConfirmation] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        requestAnimationFrame(() => {
            if (scannerRef.current && mediaType === sourceType.keyboard) {
                scannerRef.current.focus();
            }
        });
    }, [mediaType, scannerRef]);

    const columns = [
        {
            field: "delete",
            width: "10px",
            headerName: "",
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    onClick={() => {
                        deleteSingleMediaType(params.id);
                    }}
                >
                    <ClearIcon sx={styles.clearIcon} />
                </IconButton>
            ),
        },
        { field: "mediaList", headerName: t("omDistribution.mediaList").toLocaleUpperCase(), sortable: false },
    ];

    const isMediaTypeText = mediaType === sourceType.text;

    const validationSchemaEditMedia = Yup.object().shape({
        scanType: Yup.string()
            .max(volserMaxLength, t("omDistribution.maxLength", { charLength: volserMaxLength }))
            .test(
                "is-forbidden",
                t("omDistribution.duplicateMediaNo"),
                (value) => ![...addedMediaNos, ...mediaTypeList.map((item) => item.mediaList)].includes(value)
            ),
    });

    const { values, errors, touched, handleBlur, handleSubmit, setFieldValue } = useFormik({
        initialValues: {
            scanType: "",
        },
        validationSchema: validationSchemaEditMedia,
        onSubmit: () => {
            setFieldValue("scanType", "");
            onSubmit(mediaTypeList);
        },
    });

    const handleMediaTypeChange = (e) => {
        setMediaType(e.target.value);
    };

    const fileReadHandler = async (event) => {
        setIsLoading(true);
        const { csvData, fileName, hasError } = await handleFileRead(event);
        const uniqueRows = [...new Set(csvData)];
        if (!hasError) {
            uniqueRows.forEach((element) => {
                addMediaType(element);
            });
        }
        setFileFormatError({ fileName: fileName, hasError: hasError, lastRecordCount: uniqueRows.length });
        setIsLoading(false);
    };

    const addMediaType = (value) => {
        const haveDuplicates = mediaTypeList.some((item) => item.mediaList === value);
        const haveDuplicatesMain = addedMediaNos.some((item) => item === value);
        if (!haveDuplicates && !haveDuplicatesMain) {
            setShowErrorAlert(false);
            setMediaTypeList((prevData) => [...prevData, { id: uuidv4(), mediaList: value }]);
        } else {
            setShowErrorAlert(true);
        }
    };

    const deleteSingleMediaType = (id) => {
        const mediaArray = mediaTypeList.filter((val) => val.id !== id);
        setMediaTypeList(mediaArray);
        if (mediaArray.length === 0) {
            setFileFormatError({
                fileName: "",
                hasError: false,
                lastRecordCount: "",
            });
        }
    };

    const addScanTypeHandler = async () => {
        if (Object.keys(errors).length === 0) {
            if (values.scanType) {
                addMediaType(values.scanType);
                setFieldValue("scanType", "");
            } else {
                setFieldValue("scanType", "");
            }
        } else {
            setFieldValue("scanType", "");
        }
    };

    const enterToAddScan = (event) => {
        if (event.keyCode === enterKeyCode) {
            addScanTypeHandler();
        }
    };

    const clearFileError = () => {
        setFileFormatError({ fileName: "", hasError: false, lastRecordCount: "" });
    };

    const resetModal = () => {
        setMediaType(t("common.textFile"));
        clearFileError();
        setMediaTypeList([]);
        setFieldValue("scanType", "");
        setShowErrorAlert(false);
    };

    const closeHandler = () => {
        if (mediaTypeList.length > 0) {
            setShowCloseModalConfirmation(true);
        } else {
            onClose();
        }
    };

    const closeDeleteAllConfirmation = () => setShowDeleteConfirmation(false);

    const getStatusValue = () => {
        if (isMediaTypeText) {
            if (fileFormatError.fileName && !fileFormatError.hasError) {
                return t("messages.fileSuccessfullyImported", { fileName: fileFormatError.fileName });
            } else {
                if (fileFormatError.hasError) {
                    return t("messages.fileErrorMessage", { fileName: fileFormatError.fileName });
                }
            }
        }
        return " ";
    };

    const getRecordCount = () => {
        const messageKey = fileFormatError.lastRecordCount ? "full" : "partial";
        return t(`messages.lastRecordCountFromTxtFile.${messageKey}`, {
            count: mediaTypeList.length,
            lastCount: fileFormatError.lastRecordCount,
        });
    };

    const downloadInstructionFile = () => {
        setLoading(true);
        downloadInstructionGuide(staticFile["acceptableFileFormatForImportMediaFile"], importMediaFileFileName, () => {
            setLoading(false);
        });
    };

    const handleOkClick = () => {
        if (values.scanType) {
            addMediaType(values.scanType);
            setFieldValue("scanType", "");
            handleSubmit();
        } else {
            if (mediaTypeList.length > 0) {
                handleSubmit();
            } else {
                setShowErrorAlert(true);
            }
        }
    };

    return (
        <>
            <CircularLoaderFullPage show={loading} />
            <SecureSyncModal
                open={true}
                title={t("omDistribution.addMediaDistributionList")}
                maxWidth={"sm"}
                fullWidth
                footer={
                    <Box sx={styles.footerBtnSection}>
                        <Button variant="outlined" onClick={closeHandler}>
                            {t("buttonLabels.cancel")}
                        </Button>
                        <Button title={getKeyBoardLabel(shortCutKeys.ok)} variant="contained" onClick={handleOkClick}>
                            {t("buttonLabels.ok")}
                        </Button>
                    </Box>
                }
            >
                <Box sx={styles.container}>
                    <Box sx={styles.innerBox}>
                        {showErrorAlert && (
                            <Box sx={styles.marginBottomBrake}>
                                <SecureSyncAlert
                                    message={
                                        mediaTypeList.length > 0
                                            ? t("omDistribution.duplicateRecordsIdentified")
                                            : t("messages.submitEmptyMediaList", {
                                                  buttonLabel: t("buttonLabels.cancel"),
                                              })
                                    }
                                    severity="error"
                                    onClose={() => {
                                        setShowErrorAlert(false);
                                    }}
                                />
                            </Box>
                        )}
                        {isMediaTypeText ? (
                            <Typography variant="body1" sx={styles.subheader}>
                                <Trans
                                    i18nKey={"common.selectAnImportFile"}
                                    values={{ buttonLabel: t("buttonLabels.ok") }}
                                />{" "}
                            </Typography>
                        ) : (
                            <Typography variant="body1" sx={styles.subheader}>
                                <Trans i18nKey={"omDistribution.keyWedgeScanMediaNumbers"} />
                            </Typography>
                        )}
                        <br />
                        <Box display="flex" alignItems="center" justifyContent="center" gap={2} marginBottom={2}>
                            <SecureSyncSelect
                                required={true}
                                type="outlined"
                                label={t("omDistribution.mediaType")}
                                options={getSourceTypeOptions()}
                                value={mediaType}
                                onChange={handleMediaTypeChange}
                            />
                            <SecureSyncIconTooltip
                                title={
                                    <>
                                        <Typography>
                                            {t("omDistribution.mediaSourceOptions")}
                                            {isMediaTypeText ? (
                                                <Box fontSize={12}>{t("omDistribution.importATextFile")}</Box>
                                            ) : (
                                                <Box fontSize={12}>{t("omDistribution.enterMediaKeyboard")}</Box>
                                            )}
                                        </Typography>
                                    </>
                                }
                                placement="right"
                            />
                        </Box>
                        {isMediaTypeText && (
                            <Box>
                                <Typography sx={styles.importFileSection}>{t("common.importFile")} *</Typography>
                                <Box display="flex" alignItems="center" justifyContent="left" gap={2}>
                                    <SecureSyncFileUpload
                                        startIcon={<UploadIcon />}
                                        variant="contained"
                                        disabled={isLoading}
                                        onChange={fileReadHandler}
                                        accept=".txt, .csv"
                                        name={t("common.uploadFile")}
                                    />
                                    <SecureSyncIconTooltip
                                        title={t("containerPickup.fileFormatHelp")}
                                        placement="right"
                                        onClick={downloadInstructionFile}
                                    />
                                </Box>
                                {fileFormatError.hasError !== true ? (
                                    <Typography sx={styles.footNoteText}>
                                        {t("omDistribution.browseFilesToUploadTxt")}
                                    </Typography>
                                ) : (
                                    <Typography sx={styles.errorText}>{t("omDistribution.formatError")}</Typography>
                                )}
                            </Box>
                        )}
                        <SecureSyncTableCard>
                            <Box sx={styles.recordCount}>
                                {isMediaTypeText ? (
                                    mediaTypeList.length > 0 ? (
                                        <Box sx={styles.statusRecord}>
                                            <Typography variant="body1">
                                                {t("messages.recordsFound", { count: mediaTypeList.length })}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={styles.statusRecord}>
                                            <Typography variant="body1"></Typography>
                                        </Box>
                                    )
                                ) : (
                                    <Box sx={styles.addMediaSection}>
                                        <Box>
                                            <TextField
                                                fullWidth
                                                label={t("omDistribution.scanTypeHere")}
                                                name="scanType"
                                                focused
                                                required
                                                inputRef={scannerRef}
                                                size="small"
                                                value={values.scanType}
                                                onChange={(e) => {
                                                    const input = e.target.value;
                                                    if (input.length <= volserMaxLength) {
                                                        setFieldValue("scanType", input);
                                                    }
                                                }}
                                                onBlur={handleBlur}
                                                error={touched.scanType && Boolean(errors.scanType)}
                                                helperText={touched.scanType && errors.scanType}
                                                onKeyDown={(e) => enterToAddScan(e)}
                                            />
                                            <Box sx={styles.marginLeft}>
                                                <FormHelperText>{t("common.addOrEnter")}</FormHelperText>
                                            </Box>
                                        </Box>
                                        <Box sx={styles.addBtnSection}>
                                            <Button variant={"outlined"} onClick={addScanTypeHandler}>
                                                {t("buttonLabels.add")}
                                            </Button>
                                            <SecureSyncIconTooltip
                                                title={
                                                    <Typography>
                                                        {t("omDistribution.howToEnterMedia")}
                                                        <Box fontSize={12}>
                                                            {t("omDistribution.howToEnterMediaDescription")}
                                                        </Box>
                                                    </Typography>
                                                }
                                                placement="right"
                                            />
                                        </Box>
                                    </Box>
                                )}
                                <Box sx={styles.recordEnd}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            if (mediaTypeList.length > 0) {
                                                setShowDeleteConfirmation(true);
                                            }
                                        }}
                                    >
                                        {t("buttonLabels.deleteAll")}
                                    </Button>
                                </Box>
                            </Box>
                            <Box sx={styles.tblCardContent}>
                                <SecureSyncTable
                                    rows={mediaTypeList}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    tableMinWidth="100px"
                                    noDataMessage={t("omDistribution.noMediaIsAvailable")}
                                    disablePagination={true}
                                    headerHeight="36px"
                                    rowHeight="32px"
                                />
                            </Box>
                        </SecureSyncTableCard>
                        <Box marginTop={2}>
                            <SecureSyncReadOnlyTextField
                                value={mediaTypeList[mediaTypeList.length - 1]?.mediaList || " "}
                                label={t("omDistribution.lastItem")}
                            />
                        </Box>
                        <Box marginTop={2}>
                            <SecureSyncReadOnlyTextField
                                value={getStatusValue()}
                                isError={fileFormatError.hasError}
                                label={t("common.status")}
                            />
                        </Box>
                        <Box marginTop={2}>
                            <SecureSyncReadOnlyTextField
                                value={getRecordCount()}
                                label={t("omDistribution.recordCount")}
                            />
                        </Box>
                    </Box>
                </Box>
            </SecureSyncModal>
            <DeleteAllConfirmation
                open={showDeleteConfirmation}
                onClose={closeDeleteAllConfirmation}
                handleConfirm={() => {
                    setMediaTypeList([]);
                    closeDeleteAllConfirmation();
                    clearFileError();
                }}
            />
            <CloseModalConfirmation
                handleConfirm={() => {
                    onClose();
                    resetModal();
                }}
                onClose={() => {
                    setShowCloseModalConfirmation(false);
                }}
                open={showCloseModalConfirmation}
            />
        </>
    );
};

export default AddMediaModal;
